import { getImportDataActionQueueKey } from "#batteries-included-components/Buttons/ImportDataAction/ImportDataAction.helpers";
import { linkToDeviceDetail, linkToFacilities } from "#routers/links";
import { ImportDataActionStatusWithQueue } from "#src/batteries-included-components/Banners/ImportDataActionStatus";
import { ImportDataAction } from "#src/batteries-included-components/Buttons/ImportDataAction";
import PermissionAwareExportButton from "#src/batteries-included-components/Buttons/PermissionAwareExportButton/PermissionAwareExportButton";
import EditDeviceDialog from "#src/batteries-included-components/Dialogs/EditDeviceDialog";
import {
  DevicesTableFilterArea,
  DevicesViewFilterArea,
} from "#src/batteries-included-components/FilterAreas/AssetsFilterAreas";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import {
  useTableSortingAndPagination,
  useTablesSortingAndPagination,
} from "#src/components/Redux/reducers/tableStateReducer";
import { useCustomAttributeFilters } from "#src/components/hooks/FilterPanel/useCustomAttributeFilters";
import {
  useDeleteDevice,
  useExportDevices,
  useListDevices,
} from "#src/components/hooks/adapters/utils/useDevices";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import useLocalization from "#src/hooks/useLocalization";
import { useStickyState } from "#src/hooks/useStickyState";
import { useStorageKey } from "#src/hooks/useStorageKey";
import {
  useDataTableColumnConfigurationProps,
  useDataTableGroupBy,
  useDataTableGroupByProps,
  useDataTableMetaExtendedHeaders,
  useDataTableMetaHeaders,
  useDataTableUserInterfaceConfiguration,
} from "#src/utils/table";
import { useQueries } from "@tanstack/react-query";
import {
  Button,
  DataTablePanel,
  Dialog,
  getDataTableGroupByKeysForGroupByData,
  HeaderType,
  ItemAction,
  Pill,
  SortingType,
  useToast,
  type DataTableProps,
} from "@validereinc/common-components";
import {
  AssetType,
  DeviceAdapter,
  DeviceAdapterQueryKeys,
  DeviceType,
  ResourceDefinitions,
  SortDirection,
  type GroupedByEntryType,
} from "@validereinc/domain";
import {
  getYearMonthFromDateRange,
  toFlattenedObject,
  toTitleCaseString,
} from "@validereinc/utilities";
import React, { useMemo, useState } from "react";

const initialSort: SortingType = {
  sortBy: "name",
  sortDirection: SortDirection.ASCENDING,
};

const importDataQueueKey = getImportDataActionQueueKey({
  resource: ResourceDefinitions.device,
});

const DeviceList = () => {
  const [isDataIngestionEnabled] = useIsFeatureAvailable({
    featureFlagQuery: "core:data_pipeline",
  });
  const { toast } = useToast();
  const [assetsSharedViewConfigStorageKey] = useStorageKey(
    ["view-configuration"],
    ["assets", "shared"]
  );
  const [
    tableConfigStorageKey,
    filterConfigStorageKey,
    tableStateStorageKey,
    groupByTablesStateStorageKey,
  ] = useStorageKey(
    [
      "table-configuration",
      "filter-configuration",
      "table-state",
      "group-by-tables-state",
    ],
    ["devices", "list"]
  );
  const {
    saveDefaultConfigurationMutation,
    isSaveConfigurationAvailable,
    resetConfigurationMutation,
    deleteDefaultConfigurationMutation,
    isDeleteConfigurationAvailable,
    isLoading: isLoadingTableUserInterfaceConfiguration,
    matchingDefaultConfiguration,
    matchingDefaultTableUserInterfaceConfigurationsQuery,
  } = useDataTableUserInterfaceConfiguration(tableConfigStorageKey);
  const [assetIdToEdit, setAssetIdToEdit] = useState<string | undefined>();
  const [deviceToDelete, setDeviceToDelete] = useState<DeviceType | undefined>(
    undefined
  );
  const { localize } = useLocalization();
  const [viewFilters] = useStickyState({}, assetsSharedViewConfigStorageKey);
  const [tableFilters] = useStickyState({}, filterConfigStorageKey);
  const rawFilters = useMemo<any>(
    () => ({ ...tableFilters, ...viewFilters }),
    [tableFilters, viewFilters]
  );
  const { period, ...cleanFilter } = rawFilters;

  const [tableState, updateTableState] = useTableSortingAndPagination(
    initialSort,
    rawFilters,
    tableStateStorageKey
  );

  const devicesParams: Parameters<typeof DeviceAdapter.getList>[0] = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    groupBy: tableState.groupBy,
    filters: toFlattenedObject(cleanFilter),
    // @ts-expect-error TODO: Typing on filter
    meta: { period: getYearMonthFromDateRange(period) },
  };

  const devicesQuery = useListDevices(devicesParams, { enabled: true });
  const items = devicesQuery.data?.data ?? [];

  const groupByKeys = getDataTableGroupByKeysForGroupByData(
    items as unknown as GroupedByEntryType[]
  );
  const [groupByTablesStates, updateGroupByTablesStates] =
    useTablesSortingAndPagination(
      groupByKeys,
      {
        ...devicesParams,
        page: 1,
        pageSize: 10,
        sortBy: "created_at",
        sortDirection: SortDirection.DESCENDING,
      },
      groupByTablesStateStorageKey
    );

  const {
    queryPayloads: groupByQueryPayloads,
    setActiveGroupByGroups,
    totalSelectableItems,
  } = useDataTableGroupBy(
    items as unknown as GroupedByEntryType[],
    tableState.groupBy,
    groupByKeys.map((key) => groupByTablesStates.get(key) ?? devicesParams)
  );

  const devicesGroupedByQueries = useQueries({
    queries: groupByQueryPayloads.map((d) => ({
      queryKey: DeviceAdapterQueryKeys.getList(d.payload),
      queryFn: () => DeviceAdapter.getList(d.payload),
      enabled: d.enabled,
    })),
  });

  const metaHeaders = useDataTableMetaHeaders();
  const metaExtendedHeaders = useDataTableMetaExtendedHeaders(
    tableState.groupBy.length
      ? devicesGroupedByQueries.flatMap((q) => q.data?.data ?? [])
      : items
  );

  const { customAttributeHeaders, isLoading: isCustomAttributesLoading } =
    useCustomAttributeFilters({
      assetType: AssetType.DEVICE,
      section: "Device",
      groupable: true,
    });

  const { mutate: handleExport, isLoading: isExporting } =
    useExportDevices(devicesParams);

  const { mutate: deleteDevice } = useDeleteDevice({
    onSuccess: () => {
      toast.push({
        intent: "success",
        description: `Successfully deleted '${deviceToDelete?.name ?? "Device"}'`,
      });
    },
    onError: () => {
      toast.push({
        intent: "error",
        description: "Failed to delete device",
      });
    },
    onSettled: () => {
      setDeviceToDelete(undefined);
    },
  });

  const getItemActions: DataTableProps<DeviceType>["getItemActions"] = ({
    item,
  }): ItemAction[] => [
    {
      label: "Edit",
      buttonProps: {
        onClick: () => setAssetIdToEdit(item.id),
        icon: "pencil-simple",
      },
    },
    {
      label: "Delete",
      buttonProps: {
        onClick: () => setDeviceToDelete(item),
        icon: "trash",
      },
    },
  ];

  const headers: Array<HeaderType<DeviceType>> = useMemo(
    () => [
      {
        label: localize("Device"),
        key: "name",
        isSortable: true,
        renderComponent: ({ item }) => (
          <RoutingLink to={linkToDeviceDetail(item?.id)}>
            {item.name}
          </RoutingLink>
        ),
      },
      {
        label: `${localize("Device")} Type`,
        key: "type_id",
        renderComponent: ({ item }) => item?.type?.name ?? "-",
        isSortable: true,
        // REVIEW: cannot be enabled until item access matches key, and key 'type.name' to sort or group on is not valid
        // isGroupable: true,
      },
      {
        label: localize("Facility"),
        key: "facility.name",
        isGroupable: true,
        renderComponent: ({ item }: { item: DeviceType }) => (
          <RoutingLink to={linkToFacilities(item?.facility_id)}>
            {item.facility.name}
          </RoutingLink>
        ),
      },
      {
        label: "Status",
        key: "status",
        isSortable: true,
        isGroupable: true,
        renderComponent: ({ item }) => (
          <Pill variant={item.status === "active" ? "success" : "default"}>
            {toTitleCaseString(item.status, { intent: "ui" })}
          </Pill>
        ),
      },
      ...customAttributeHeaders,
      ...metaHeaders,
      ...metaExtendedHeaders,
    ],
    [localize, customAttributeHeaders, metaHeaders, metaExtendedHeaders]
  );

  const groupByDataTableProps = useDataTableGroupByProps({
    items,
    groupBy: tableState.groupBy,
    updateTableState,
    setActiveGroupByGroups,
    groupByQueries: devicesGroupedByQueries,
    groupByTablesStates,
    updateGroupByTablesStates,
    headers,
    getItemActions,
    getItemId: (item) => item.id,
    totalSelectableItems,
  });
  const columnConfigurationDataTableProps =
    useDataTableColumnConfigurationProps({
      configurationName: "Default for Devices list table",
      saveDefaultConfigurationMutation,
      isSaveConfigurationAvailable,
      resetConfigurationMutation,
      deleteDefaultConfigurationMutation,
      isDeleteConfigurationAvailable,
      isLoading: isLoadingTableUserInterfaceConfiguration,
      matchingDefaultConfiguration,
      matchingDefaultTableUserInterfaceConfigurationsQuery,
    });

  return (
    <>
      {isDataIngestionEnabled ? (
        <ImportDataActionStatusWithQueue queueKey={importDataQueueKey} />
      ) : null}
      <DevicesViewFilterArea
        viewConfigStorageKey={assetsSharedViewConfigStorageKey}
      />
      <DataTablePanel
        storageKey={tableConfigStorageKey}
        panelProps={{
          actionRow: [
            <PermissionAwareExportButton
              key="export-devices"
              onClick={handleExport}
              isExporting={isExporting}
            />,
            isDataIngestionEnabled ? (
              <ImportDataAction
                key="import-device"
                resource={ResourceDefinitions.device}
              />
            ) : null,
          ],
        }}
        filterComponent={
          <DevicesTableFilterArea
            filterConfigStorageKey={filterConfigStorageKey}
            hasCustomAttributeFilters
          />
        }
        columnConfigurationProps={columnConfigurationDataTableProps}
        dataTableProps={{
          intent: "simplicity-first",
          headers,
          items: (devicesQuery.data as any)?.data ?? [],
          getItemId: (item) => item.id,
          isLoading: devicesQuery.isLoading || isCustomAttributesLoading,
          isBusy: devicesQuery.isFetching,
          onSortChange: updateTableState,
          sorting: {
            sortBy: tableState.sortBy,
            sortDirection: tableState.sortDirection,
          },
          onPaginationChange: updateTableState,
          pagination: {
            page: tableState.page,
            pageSize: tableState.pageSize,
            total: (devicesQuery.data as any)?.total_entries,
            pageSizeText: "rows per page",
          },
          getItemActions,
        }}
        {...groupByDataTableProps}
      />
      <Dialog
        isOpen={!!deviceToDelete}
        onClose={() => setDeviceToDelete(undefined)}
        title={`Delete "${deviceToDelete?.name}"`}
        actionRow={[
          <Button
            key="delete-dialog-action"
            onClick={() => {
              deleteDevice(deviceToDelete?.id ?? "");
            }}
            variant="error"
          >
            Delete
          </Button>,
        ]}
      >
        <div>
          Are you sure you want to delete this device? This action cannot be
          undone.
        </div>
      </Dialog>
      <EditDeviceDialog
        onClose={() => setAssetIdToEdit(undefined)}
        deviceId={assetIdToEdit}
        onEdit={() => {
          devicesQuery.refetch();
        }}
      />
    </>
  );
};

export default DeviceList;
